import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { dataApi } from '@/lib/api';
import { Users, Target, Rocket, ClipboardList, BarChart3, ArrowRight, TrendingUp } from 'lucide-react';

function formatNum(n) {
  if (!n) return '0';
  if (n >= 1000000) return (n / 1000000).toFixed(1) + 'M';
  if (n >= 1000) return (n / 1000).toFixed(1) + 'k';
  return n?.toLocaleString();
}

export default function HomePage() {
  const [stats, setStats] = useState(null);
  const [topAccounts, setTopAccounts] = useState([]);

  useEffect(() => {
    Promise.all([
      dataApi.getAudienceIntelligence('Financial Services', 90),
      dataApi.getCampaigns({ page: 1 }),
      dataApi.getTopAccounts(),
    ]).then(([ai, camp, ta]) => {
      setStats({
        prospects: ai.data.total_prospects,
        engagements: ai.data.total_engagements,
        leads: camp.data.kpis?.total_leads,
        campaigns: camp.data.total,
      });
      setTopAccounts(ta.data || []);
    }).catch(() => {});
  }, []);

  const cards = [
    { title: 'Audience Intelligence', desc: 'In-depth audience analysis with intent signals, topics, and content insights.', icon: Users, to: '/audience-intelligence', color: 'bg-orange-50 text-[#F66922]' },
    { title: 'Priority Accounts', desc: 'Identify and prioritize high-value accounts with engagement scoring.', icon: Target, to: '/priority-accounts', color: 'bg-emerald-50 text-emerald-600' },
    { title: 'Campaign Execution', desc: 'Track campaign performance, targets, and seller outcomes.', icon: Rocket, to: '/campaigns', color: 'bg-blue-50 text-blue-600' },
    { title: 'Leads Management', desc: 'Manage, assign, and track leads through the pipeline.', icon: ClipboardList, to: '/leads', color: 'bg-violet-50 text-violet-600' },
    { title: 'Analytics', desc: 'Engagement trends, funnel analysis, and geography insights.', icon: BarChart3, to: '/analytics', color: 'bg-pink-50 text-pink-600' },
  ];

  return (
    <div className="space-y-8" data-testid="home-page">
      <div>
        <h1 className="text-3xl font-semibold text-[#0A0A0A] tracking-tight">Welcome to OASIS</h1>
        <p className="text-sm text-neutral-400 mt-1">B2B demand generation intelligence at your fingertips</p>
      </div>

      {/* Quick Stats */}
      {stats && (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          {[
            { label: 'Total Prospects', value: formatNum(stats.prospects), icon: TrendingUp },
            { label: 'Engagements', value: formatNum(stats.engagements), icon: BarChart3 },
            { label: 'Active Leads', value: formatNum(stats.leads), icon: ClipboardList },
            { label: 'Campaigns', value: formatNum(stats.campaigns), icon: Rocket },
          ].map((s, i) => (
            <div key={i} className="oaa-card p-4">
              <div className="flex items-center justify-between">
                <p className="text-[11px] uppercase tracking-wider font-semibold text-neutral-400">{s.label}</p>
                <s.icon className="w-4 h-4 text-neutral-300" />
              </div>
              <p className="text-2xl font-bold text-[#0A0A0A] mt-2 tabular-nums">{s.value}</p>
            </div>
          ))}
        </div>
      )}

      {/* Top Scored Accounts */}
      {topAccounts.length > 0 && (
        <div className="oaa-card p-6" data-testid="top-accounts-widget">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-base font-bold text-[#0A0A0A] tracking-tight">Hottest Accounts This Week</h3>
            <Link to="/priority-accounts" className="text-[11px] font-semibold text-[#F66922] uppercase tracking-wide hover:underline">View all</Link>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-5 gap-3">
            {topAccounts.map((a, i) => {
              const tierColor = a.tier === 'Hot' ? 'bg-[#F66922] text-white' : a.tier === 'Warm' ? 'bg-orange-100 text-orange-700' : 'bg-neutral-100 text-neutral-600';
              return (
                <Link key={i} to={`/account/${encodeURIComponent(a.company)}`}
                  className="p-3 rounded-lg border border-neutral-100 hover:border-[#F66922]/30 hover:bg-orange-50/30 transition-all group">
                  <div className="flex items-center justify-between mb-2">
                    <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${tierColor}`}>{a.score}</span>
                    <span className="text-[10px] text-neutral-300 uppercase font-bold">{a.tier}</span>
                  </div>
                  <p className="text-sm font-semibold text-[#0A0A0A] truncate group-hover:text-[#F66922] transition-colors">{a.company}</p>
                  <p className="text-[10px] text-neutral-400 mt-1">{a.industry} / {a.primary_location}</p>
                  <div className="flex gap-3 mt-2 text-[10px] text-neutral-400">
                    <span>{a.total_signals} signals</span>
                    <span>{a.departments_engaged} depts</span>
                  </div>
                </Link>
              );
            })}
          </div>
        </div>
      )}

      {/* Navigation Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {cards.map((card, i) => (
          <Link key={i} to={card.to} className="oaa-card p-5 hover:shadow-md transition-shadow group" data-testid={`nav-card-${i}`}>
            <div className={`w-10 h-10 rounded-lg ${card.color} flex items-center justify-center mb-3`}>
              <card.icon className="w-5 h-5" />
            </div>
            <h3 className="text-base font-semibold text-[#0A0A0A] mb-1 group-hover:text-[#F66922] transition-colors">{card.title}</h3>
            <p className="text-xs text-neutral-400 leading-relaxed mb-3">{card.desc}</p>
            <div className="flex items-center gap-1 text-xs font-medium text-[#F66922]">
              Open <ArrowRight className="w-3 h-3" />
            </div>
          </Link>
        ))}
      </div>
    </div>
  );
}
